// helix-exception: dense badge text drawn at 8-11px in Figma has no Helix type-scale step (F-12)
import { Check, InfoCircle, Pen, UserCircle } from 'flowbite-react-icons/outline'

import {
  CLICKABLE_CARD_SURFACE_CLASS,
  STATUS_CLICKABLE_CARD_SURFACE_CLASS,
  editButtonHoverFillClass,
  withStopPropagation,
} from './cardSurface'

export const sectionCardTitleClass =
  'flex-1 text-lg font-semibold leading-7 tracking-tight text-text-heading dark:text-white sm:text-2xl sm:leading-8'

/** Title class for terms / disclosure cards. Export only — consumed by a later
 *  cell and never applied inside FormCard. */
export const sectionCardTitleH6 =
  'text-lg font-semibold leading-6 tracking-tight text-text-heading dark:text-white'

interface FormCardProps {
  title: string
  children: React.ReactNode
  needsAttention?: boolean
  /**
   * R5: section is required, so the card shows the purple left accent bar while
   * it is outstanding. Unlike `needsAttention`, this does not promote the Edit
   * button to the brand-filled style. The accent is dropped once `completed` is
   * set, so purple only flags required work that still needs input.
   */
  required?: boolean
  /**
   * When true, a `required` section's purple accent bar is suppressed because
   * its data is complete. Ignored for optional sections (which never show the
   * required accent) and overridden by `needsAttention`.
   */
  completed?: boolean
  onEdit?: () => void
  editLabel?: string
  /**
   * Optional secondary action(s) rendered in the header row, left of the Edit
   * button (e.g. a lower-emphasis "Add Phenotypes" pill). On a clickable card
   * surface these controls own their own click handlers, so they must be
   * wrapped with `withStopPropagation` by the caller. Wrapped here in a
   * propagation-stopping container as a defensive backstop.
   */
  headerActions?: React.ReactNode
  badge?: React.ReactNode
  /** Merged onto the outer card container (e.g. `h-full` for equal-height flex rows). */
  className?: string
  /**
   * R5: make the whole card a click target that opens the section editor
   * (hover lights it up). Only safe for pure summary cards — children with their
   * own click handlers must use `withStopPropagation`. Requires `onEdit`.
   */
  clickableSurface?: boolean
  /**
   * `status` (comparator section cards, GEN-5138 design): a required,
   * incomplete card shows a brand-outlined Edit at rest, and hover tints the
   * card brand-softer (no brand border). `default` keeps the GEN-4260 rule.
   */
  appearance?: 'default' | 'status'
}

export function FormCard({
  title,
  children,
  needsAttention = false,
  required = false,
  completed = false,
  onEdit,
  editLabel = 'Edit',
  headerActions,
  badge,
  className,
  clickableSurface = false,
  appearance = 'default',
}: FormCardProps) {
  const showAccentBar = needsAttention || (required && !completed)
  const cardClasses = showAccentBar ? 'order-card-attention !p-4 sm:!p-7' : 'order-card !p-4 sm:!p-7'
  const isClickable = Boolean(clickableSurface && onEdit)
  const isStatus = appearance === 'status'
  const surfaceClass = isStatus ? STATUS_CLICKABLE_CARD_SURFACE_CLASS : CLICKABLE_CARD_SURFACE_CLASS
  const editToneClass = needsAttention
    ? 'bg-brand-600 text-white hover:bg-brand-700'
    : isStatus && showAccentBar
      ? 'border border-border-brand dark:border-brand-500 bg-white text-text-fg-brand hover:border-transparent hover:bg-brand-600 hover:text-white dark:bg-gray-800 dark:text-brand-300'
      : 'border border-border-base bg-white text-text-body hover:bg-background-secondary-medium dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'

  return (
    <div
      className={`${cardClasses} w-full min-w-0${isClickable ? ` ${surfaceClass}` : ''}${className ? ` ${className}` : ''}`}
      onClick={isClickable ? onEdit : undefined}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <h2 className={sectionCardTitleClass}>
            {title}
          </h2>
          {headerActions ? (
            <div
              className="flex items-center gap-2"
              onClick={isClickable ? (e) => e.stopPropagation() : undefined}
            >
              {headerActions}
            </div>
          ) : null}
          {onEdit && (
            <button
              type="button"
              onClick={isClickable ? withStopPropagation(onEdit) : onEdit}
              className={`inline-flex items-center justify-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium transition-colors sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs ${editToneClass}${
                isClickable ? ` ${editButtonHoverFillClass(needsAttention)}` : ''
              }`}
            >
              <Pen className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              {editLabel}
            </button>
          )}
        </div>

        <div className="flex flex-col gap-4">{children}</div>

        {badge && (
          <div className="mt-1" onClick={isClickable ? (e) => e.stopPropagation() : undefined}>
            {badge}
          </div>
        )}
      </div>
    </div>
  )
}

interface AiBadgeProps {
  text: string
  timestamp?: string
  onViewSource?: () => void
  variant?: 'brand' | 'success'
}

export function AiBadge({ text, timestamp, onViewSource, variant = 'brand' }: AiBadgeProps) {
  const isSuccess = variant === 'success'
  const containerClasses = isSuccess
    ? 'border-success-500 dark:border-success-700 bg-success-50 dark:bg-gray-900 text-success-700 dark:text-success-100'
    : 'border-border-brand dark:border-brand-500 bg-white dark:bg-gray-900 text-text-fg-info dark:text-brand-300'

  return (
    <div
      className={`inline-flex w-fit items-center gap-2 self-start rounded-full border py-1 pl-1 pr-3 ${containerClasses}`}
    >
      {isSuccess ? (
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-success-100 dark:bg-gray-900 text-success-600">
          <Check className="h-3 w-3" />
        </span>
      ) : (
        <div className="alert-pill-badge">
          <span className="text-xs font-medium text-white">AI</span>
        </div>
      )}
      <span className={`text-xs sm:text-sm ${isSuccess ? 'font-medium' : ''}`}>
        {text}
        {timestamp && <span className="ml-1">{timestamp}</span>}
        {onViewSource && (
          <button onClick={onViewSource} className="ml-2 text-text-body dark:text-gray-400 hover:underline">
            View Source
          </button>
        )}
      </span>
    </div>
  )
}

interface StatusPillProps {
  text: string
  variant?: 'success' | 'info' | 'neutral'
  /** Leading icon when variant is success (defaults to checkmark). */
  successLead?: 'check' | 'user'
  /** Info variant only: size to content instead of spanning the card (GEN-5138 design). */
  chip?: boolean
}

export function StatusPill({ text, variant = 'info', successLead = 'check', chip = false }: StatusPillProps) {
  const isSuccess = variant === 'success'
  const isNeutral = variant === 'neutral'

  // Completed pill (GEN-5138 design): content-width soft-green pill with the
  // glyph in a round success chip badge, mirroring the info hint pill's shape.
  if (isSuccess) {
    const Lead = successLead === 'user' ? UserCircle : Check
    return (
      <div className="inline-flex w-fit items-center gap-2 self-start rounded-full border border-success-500/30 bg-success-50 dark:bg-gray-900 py-1 pl-1 pr-3 text-success-700 dark:border-success-500/40 dark:text-success-100">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success-100 dark:bg-success-500/30">
          <Lead className="h-3.5 w-3.5" aria-hidden />
        </span>
        <span className="text-sm font-medium leading-5">{text}</span>
      </div>
    )
  }

  // Info empty-state hint (UX-045, Figma 2111:10688): a full-width, fully-rounded
  // brand-soft pill with a solid info glyph in a brand chip badge on the left —
  // NOT the bordered small-alert.
  if (!isNeutral) {
    return (
      <div className={`inline-flex ${chip ? 'w-fit self-start' : 'w-full'} items-center gap-2 rounded-full border border-border-brand-subtle bg-background-brand-softer py-1 pl-1 pr-2 text-text-fg-info dark:border-brand-500/40 dark:bg-brand-950/40 dark:text-sky-200`}>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-500/30">
          <InfoCircle className="h-4 w-4" aria-hidden />
        </span>
        <span className="min-w-0 text-sm font-medium leading-5">{text}</span>
      </div>
    )
  }

  // Neutral / Optional (GEN-5138 design, UX-015 gray): same chip-badge shape
  // with a gray info glyph.
  return (
    <div className="inline-flex w-fit items-center gap-2 self-start rounded-full border border-border-base bg-white py-1 pl-1 pr-3 text-text-heading dark:border-gray-700 dark:bg-gray-800 dark:text-white">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-quaternary text-text-body dark:bg-gray-700 dark:text-gray-300">
        <InfoCircle className="h-3.5 w-3.5" aria-hidden />
      </span>
      <span className="text-sm font-medium leading-5">{text}</span>
    </div>
  )
}
